import gsap from 'gsap';
import { createCamstLoadTracker } from './camst-page-load.js';
import { primeCamstHeroVideos, runCamstHeroReveal } from './camst-hero.js';

var CIRCLE_DELAYS = [0, 0.05, 0.02, 0.08, 0.03, 0.06, 0.04];
var DEFAULT_ANIMATION_DURATION = 3.2;
var DEFAULT_MAX_DURATION = 6;
var DEFAULT_START_DELAY = 0.8;
var PRELOADER_SEEN_KEY = 'camst_preloader_seen';

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getPreloaderFrequency(root) {
  var frequency = String((root && root.dataset.frequency) || 'session');

  if (frequency === 'always' || frequency === 'session' || frequency === 'once') {
    return frequency;
  }

  return 'session';
}

function hasSeenPreloader(frequency) {
  try {
    if (frequency === 'session') {
      return window.sessionStorage.getItem(PRELOADER_SEEN_KEY) === '1';
    }

    if (frequency === 'once') {
      return window.localStorage.getItem(PRELOADER_SEEN_KEY) === '1';
    }
  } catch (err) {
    return false;
  }

  return false;
}

function markPreloaderSeen(frequency) {
  try {
    if (frequency === 'session') {
      window.sessionStorage.setItem(PRELOADER_SEEN_KEY, '1');
    } else if (frequency === 'once') {
      window.localStorage.setItem(PRELOADER_SEEN_KEY, '1');
    }
  } catch (err) {
    // Ignore private-mode / storage quota errors.
  }
}

function getPreloaderConfig(root) {
  var animationDuration = parseFloat(root.dataset.animationDuration || String(DEFAULT_ANIMATION_DURATION));
  var maxDuration = parseFloat(root.dataset.maxDuration || String(DEFAULT_MAX_DURATION));
  var startDelay = parseFloat(root.dataset.startDelay || String(DEFAULT_START_DELAY));

  animationDuration = Math.max(2, animationDuration || DEFAULT_ANIMATION_DURATION);
  maxDuration = Math.max(3, maxDuration || DEFAULT_MAX_DURATION);
  startDelay = Math.max(0, startDelay || 0);

  if (maxDuration < animationDuration + startDelay) {
    maxDuration = animationDuration + startDelay + 1;
  }

  return {
    animationDuration: animationDuration,
    maxDuration: maxDuration,
    startDelay: startDelay,
  };
}

function shouldRunPreloader(root) {
  var allowed = (root.dataset.pageIds || '')
    .split(',')
    .map(function (id) {
      return id.trim();
    })
    .filter(Boolean);

  // Nessuna pagina selezionata in Opzioni = tutte le pagine frontend.
  if (!allowed.length) {
    return true;
  }

  var current = String(root.dataset.currentPageId || '');

  return allowed.indexOf(current) !== -1;
}

function getPreloaderShell(root) {
  return root.closest('#camst-preloader-root') || root;
}

function dispatchCamstEvent(name, detail) {
  if (!window.CustomEvent) {
    return;
  }

  window.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
}

function hidePreloaderCircles(circles) {
  gsap.killTweensOf(circles);
  gsap.set(circles, {
    autoAlpha: 0,
    scale: 0,
    pointerEvents: 'none',
    visibility: 'hidden',
  });
}

function runHandoff() {
  document.body.classList.add('camst-preloader-handoff');
  primeCamstHeroVideos();
  document.querySelectorAll('[data-camst-hero]').forEach(function (heroRoot) {
    runCamstHeroReveal(heroRoot);
  });
  dispatchCamstEvent('camst:preloader:handoff', {});
}

function runReactLikeIntro(root, onComplete) {
  var config = getPreloaderConfig(root);
  var circles = root.querySelectorAll('[data-camst-preloader-circle]');
  var progress = root.querySelector('[data-camst-preloader-progress]');
  var logo = root.querySelector('[data-camst-preloader-logo]');
  var centerConfig = {
    top: '50%',
    left: '50%',
    xPercent: -50,
    yPercent: -50,
    x: 0,
    y: 0,
    ease: 'none',
    duration: 0.8,
    scale: 0.14,
    transformOrigin: 'center center',
  };

  gsap.set(circles, { autoAlpha: 1, scale: 1, visibility: 'visible' });

  var tl = gsap.timeline({
    delay: config.startDelay,
    onComplete: onComplete,
  });

  if (progress) {
    gsap.set(progress, { scaleX: 0, transformOrigin: 'left center' });
    tl.fromTo(progress, { scaleX: 0 }, { scaleX: 1, duration: 1.7, ease: 'none' }, 0);
  }

  circles.forEach(function (circle, index) {
    gsap.set(circle, { transformOrigin: 'center center' });
    tl.to(circle, centerConfig, 0.4 + (CIRCLE_DELAYS[index] || 0));
  });

  tl.add(runHandoff, 1.3);

  tl.to(
    circles,
    {
      autoAlpha: 0,
      scale: 0,
      duration: 0.3,
      ease: 'power3.in',
      onStart: function () {
        gsap.killTweensOf(circles);
      },
      onComplete: function () {
        hidePreloaderCircles(circles);
      },
    },
    1.3
  );

  if (logo) {
    tl.to(
      logo,
      {
        opacity: 0,
        scale: 0.8,
        y: -50,
        duration: 0.6,
        ease: 'power2.inOut',
      },
      1.7
    );
  }

  var natural = tl.totalDuration();
  var target = Math.max(2.5, config.animationDuration);

  if (natural > 0 && target > 0) {
    tl.timeScale(natural / target);
  }

  return function cleanup() {
    tl.kill();
    hidePreloaderCircles(circles);
  };
}

function createPreloaderState(config) {
  return {
    animationComplete: false,
    assetsReady: document.readyState === 'complete',
    loadProgress: document.readyState === 'complete' ? 1 : 0,
    closing: false,
    finished: false,
    maxTimer: null,
    introCleanup: null,
    config: config,
  };
}

function clearPreloaderMaxTimer(state) {
  if (state && state.maxTimer) {
    window.clearTimeout(state.maxTimer);
    state.maxTimer = null;
  }
}

function startPreloaderMaxTimer(root, state) {
  clearPreloaderMaxTimer(state);

  if (!state || !state.config) {
    return;
  }

  state.maxTimer = window.setTimeout(function () {
    state.animationComplete = true;
    state.assetsReady = true;
    state.loadProgress = 1;
    maybeFinishPreloader(root, state);
  }, Math.round(state.config.maxDuration * 1000));
}

function maybeFinishPreloader(root, state) {
  if (!state || state.finished || state.closing || !state.animationComplete || !state.assetsReady) {
    return;
  }

  state.closing = true;
  finishPreloader(root, state);
}

function completePreloaderState(root, state) {
  if (state) {
    state.finished = true;
  }

  root.dataset.camstPreloaderComplete = 'true';
  root.classList.add('is-complete');
  document.body.classList.remove('camst-preloader-pending');
  document.body.classList.remove('camst-preloader-handoff');
  document.body.classList.add('camst-preloader-complete');
  markPreloaderSeen(getPreloaderFrequency(root));
}

function dismissPreloaderOverlay(shell, root, state) {
  if (!shell || shell.dataset.camstPreloaderDismissed === 'true') {
    completePreloaderState(root, state);
    dispatchCamstEvent('camst:preloader:closed', {});
    dispatchCamstEvent('camst:preloader:complete', {});
    return;
  }

  shell.dataset.camstPreloaderDismissed = 'true';

  if (prefersReducedMotion()) {
    shell.style.display = 'none';
    shell.setAttribute('aria-hidden', 'true');
    completePreloaderState(root, state);
    dispatchCamstEvent('camst:preloader:closed', {});
    dispatchCamstEvent('camst:preloader:complete', {});
    return;
  }

  gsap.to(shell, {
    opacity: 0,
    duration: 0.22,
    ease: 'power2.out',
    onComplete: function () {
      shell.style.display = 'none';
      shell.setAttribute('aria-hidden', 'true');
      completePreloaderState(root, state);
      dispatchCamstEvent('camst:preloader:closed', {});
      dispatchCamstEvent('camst:preloader:complete', {});
    },
  });
}

function finishPreloader(root, state) {
  if (!root || root.dataset.camstPreloaderComplete === 'true') {
    return;
  }

  clearPreloaderMaxTimer(state);

  if (state.introCleanup) {
    state.introCleanup();
    state.introCleanup = null;
  }

  root.dataset.camstPreloaderComplete = 'pending';

  var shell = getPreloaderShell(root);
  var progress = root.querySelector('[data-camst-preloader-progress]');

  if (progress) {
    gsap.set(progress, { scaleX: 1, transformOrigin: 'left center' });
  }

  dismissPreloaderOverlay(shell, root, state);
}

function installPreloaderFailsafe(root, state) {
  window.addEventListener(
    'load',
    function () {
      window.setTimeout(function () {
        if (
          !root ||
          root.dataset.camstPreloaderComplete === 'true' ||
          root.dataset.camstPreloaderComplete === 'pending'
        ) {
          return;
        }

        state.animationComplete = true;
        state.assetsReady = true;
        state.loadProgress = 1;
        maybeFinishPreloader(root, state);
      }, 9000);
    },
    { once: true }
  );
}

function initPreloader(root) {
  if (!root || root.dataset.camstPreloaderReady === 'true') {
    return;
  }

  if (root.dataset.camstPreloaderSkipped === 'true') {
    return;
  }

  var shell = getPreloaderShell(root);

  if (shell && shell.dataset.camstPreloaderSkipped === 'true') {
    return;
  }

  if (!shouldRunPreloader(root)) {
    return;
  }

  var frequency = getPreloaderFrequency(root);

  if (hasSeenPreloader(frequency)) {
    root.dataset.camstPreloaderSkipped = 'true';
    root.dataset.camstPreloaderComplete = 'true';
    document.body.classList.remove('camst-preloader-pending', 'camst-preloader-handoff');
    document.body.classList.add('camst-preloader-complete');

    if (shell) {
      shell.style.display = 'none';
      shell.setAttribute('aria-hidden', 'true');
      shell.dataset.camstPreloaderSkipped = 'true';
    }

    return;
  }

  root.dataset.camstPreloaderReady = 'true';
  document.body.classList.add('camst-preloader-pending');

  var config = getPreloaderConfig(root);
  var stateRef = null;

  var loadTracker = createCamstLoadTracker({
    preloaderRoot: root,
    scope: document,
    onProgress: function (progress) {
      if (!stateRef) {
        return;
      }

      stateRef.loadProgress = progress;

      if (progress >= 1) {
        stateRef.assetsReady = true;
      }
    },
  });

  if (prefersReducedMotion()) {
    var reducedMotionState = createPreloaderState(config);
    stateRef = reducedMotionState;
    startPreloaderMaxTimer(root, reducedMotionState);
    loadTracker.start().then(function () {
      reducedMotionState.assetsReady = true;
      reducedMotionState.loadProgress = 1;
      reducedMotionState.animationComplete = true;
      maybeFinishPreloader(root, reducedMotionState);
    });
    installPreloaderFailsafe(root, reducedMotionState);
    return;
  }

  var state = createPreloaderState(config);
  stateRef = state;
  startPreloaderMaxTimer(root, state);
  installPreloaderFailsafe(root, state);

  loadTracker.start().then(function () {
    state.assetsReady = true;
    state.loadProgress = 1;
    maybeFinishPreloader(root, state);
  });

  window.requestAnimationFrame(function () {
    state.introCleanup = runReactLikeIntro(root, function () {
      state.animationComplete = true;
      maybeFinishPreloader(root, state);
    });
  });
}

export function initCamstPreloader(context) {
  var scope = context || document;
  var roots = scope.querySelectorAll('[data-camst-preloader]');

  roots.forEach(function (root) {
    initPreloader(root);
  });
}
